import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { API } from '@/lib/api';

const AuthContext = createContext(null);
const ACTIVE_WORKSPACE_STORAGE_PREFIX = 'advoq_active_workspace_v1_';
const PERSONAL_WORKSPACE_KEY = 'personal';

function buildWorkspaceStorageKey(userId) {
  return `${ACTIVE_WORKSPACE_STORAGE_PREFIX}${userId || 'guest'}`;
}

function readStoredWorkspaceKey(userId) {
  if (typeof window === 'undefined' || !userId) return PERSONAL_WORKSPACE_KEY;
  return window.localStorage.getItem(buildWorkspaceStorageKey(userId)) || PERSONAL_WORKSPACE_KEY;
}

function writeStoredWorkspaceKey(userId, workspaceKey) {
  if (typeof window === 'undefined' || !userId) return;
  window.localStorage.setItem(buildWorkspaceStorageKey(userId), workspaceKey || PERSONAL_WORKSPACE_KEY);
}

function buildPersonalWorkspace() {
  return {
    key: PERSONAL_WORKSPACE_KEY,
    type: 'personal',
    label: 'Personal',
  };
}

function normalizeTeamWorkspace(team) {
  const teamId = String(team?.team_id || '').trim();
  if (!teamId) return null;
  const memberCount = Number(team?.member_count)
    || (Array.isArray(team?.members) ? team.members.length : 0);

  return {
    key: `team:${teamId}`,
    type: 'team',
    teamId,
    label: String(team?.name || 'Team').trim() || 'Team',
    memberCount,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(null);
  const [teamWorkspaces, setTeamWorkspaces] = useState([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);
  const [activeWorkspaceKey, setActiveWorkspaceKey] = useState(PERSONAL_WORKSPACE_KEY);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setSessionToken(token);
      } else {
        setUser(null);
        localStorage.removeItem('session_token');
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loadTeamWorkspaces = useCallback(async (nextUser = user, nextToken = sessionToken) => {
    const runtimeMemberships = Array.isArray(nextUser?.team_memberships)
      ? nextUser.team_memberships.map(normalizeTeamWorkspace).filter(Boolean)
      : [];

    if (!nextUser?.user_id) {
      setTeamWorkspaces([]);
      setWorkspacesLoading(false);
      return [];
    }

    if (runtimeMemberships.length) {
      setTeamWorkspaces(runtimeMemberships);
      setWorkspacesLoading(false);
      return runtimeMemberships;
    }

    setWorkspacesLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = nextToken || localStorage.getItem('session_token');
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${API}/teams/my`, {
        credentials: 'include',
        headers,
      });
      if (!response.ok) throw new Error('Failed to load teams');
      const payload = await response.json();
      const nextTeams = Array.isArray(payload?.teams)
        ? payload.teams.map(normalizeTeamWorkspace).filter(Boolean)
        : [];
      setTeamWorkspaces(nextTeams);
      return nextTeams;
    } catch {
      setTeamWorkspaces([]);
      return [];
    } finally {
      setWorkspacesLoading(false);
    }
  }, [sessionToken, user]);

  useEffect(() => {
    let cancelled = false;

    const syncTeamWorkspaces = async () => {
      if (!user?.user_id) {
        setTeamWorkspaces([]);
        setActiveWorkspaceKey(PERSONAL_WORKSPACE_KEY);
        return;
      }

      const nextTeams = await loadTeamWorkspaces(user, sessionToken);
      if (cancelled) return;

      const availableKeys = new Set([
        PERSONAL_WORKSPACE_KEY,
        ...nextTeams.map((workspace) => workspace.key),
      ]);
      const storedWorkspaceKey = readStoredWorkspaceKey(user.user_id);
      setActiveWorkspaceKey(availableKeys.has(storedWorkspaceKey) ? storedWorkspaceKey : PERSONAL_WORKSPACE_KEY);
    };

    syncTeamWorkspaces();
    return () => {
      cancelled = true;
    };
  }, [loadTeamWorkspaces, sessionToken, user]);

  useEffect(() => {
    if (!user?.user_id) return;
    writeStoredWorkspaceKey(user.user_id, activeWorkspaceKey);
  }, [activeWorkspaceKey, user?.user_id]);

  const login = (userData, token) => {
    setUser(userData);
    setSessionToken(token);
    if (token) localStorage.setItem('session_token', token);
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch {}
    setUser(null);
    setSessionToken(null);
    setTeamWorkspaces([]);
    setWorkspacesLoading(false);
    setActiveWorkspaceKey(PERSONAL_WORKSPACE_KEY);
    localStorage.removeItem('session_token');
  };

  const getAuthHeaders = useCallback(() => {
    const token = sessionToken || localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  }, [sessionToken]);

  const availableWorkspaces = useMemo(() => [
    buildPersonalWorkspace(),
    ...teamWorkspaces,
  ], [teamWorkspaces]);

  const activeWorkspace = useMemo(() => (
    availableWorkspaces.find((workspace) => workspace.key === activeWorkspaceKey)
      || availableWorkspaces[0]
      || null
  ), [activeWorkspaceKey, availableWorkspaces]);

  const defaultTeamWorkspace = useMemo(() => (
    availableWorkspaces.find((workspace) => workspace.type === 'team') || null
  ), [availableWorkspaces]);

  const hasTeamWorkspace = Boolean(defaultTeamWorkspace);

  const setActiveWorkspace = useCallback((nextWorkspace) => {
    if (!nextWorkspace) {
      setActiveWorkspaceKey(PERSONAL_WORKSPACE_KEY);
      return;
    }

    const nextKey = typeof nextWorkspace === 'string'
      ? nextWorkspace
      : nextWorkspace.key;

    if (!nextKey) {
      setActiveWorkspaceKey(PERSONAL_WORKSPACE_KEY);
      return;
    }

    setActiveWorkspaceKey(nextKey);
  }, []);

  const value = useMemo(() => ({
    user,
    setUser,
    loading,
    login,
    logout,
    checkAuth,
    getAuthHeaders,
    sessionToken,
    teamWorkspaces,
    workspacesLoading,
    availableWorkspaces,
    activeWorkspace,
    setActiveWorkspace,
    hasTeamWorkspace,
    defaultTeamWorkspace,
    refreshTeamWorkspaces: loadTeamWorkspaces,
  }), [
    activeWorkspace,
    availableWorkspaces,
    checkAuth,
    defaultTeamWorkspace,
    getAuthHeaders,
    hasTeamWorkspace,
    loadTeamWorkspaces,
    loading,
    login,
    logout,
    sessionToken,
    setActiveWorkspace,
    teamWorkspaces,
    user,
    workspacesLoading,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
